import { z } from "zod";

import { userTaxCategory } from "../../../constant/enum";
import { IBranch } from "../../../general/type/branch.types";

// Base user fields that are common to both request and response
const BaseUserSchema = z.object({
  name: z.string().min(3, "Name must be more than 3 characters"),
  icNumber: z.string().regex(/^\d{12}$/, "IC number must be 12 digits"),
  address: z.string().optional(),
  phoneNumber: z
    .string()
    .min(10, "Phone number must be 10 or 11 digits")
    .max(11, "Phone number must be 10 or 11 digits"),
  email: z.string().email("Invalid email"),
  role: z
    .enum(["admin", "branchAdmin", "lawyer", "staff", "client", "agent"])
    .default("client"),
  isEmailVerified: z.boolean().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

// Schema for password validation
const passwordSchema = z
  .string()
  .min(8, "Password need to be more than 7 chars")
  .regex(
    /^(?=.*[a-z])(?=.*\d)[A-Za-z\d]{8,}$/,
    "Password must contain at least 1 letter and 1 number"
  );

// Schema for creating/updating users
export const UserRequestSchema = BaseUserSchema.extend({
  password: z.union([
    passwordSchema,
    z.string().length(0), // Allow empty string for updates
    z.undefined(), // Allow undefined for updates
  ]),
  branchId: z
    .string()
    .regex(/^[0-9a-fA-F]{24}$/, "Invalid branch ID")
    .optional(),
  branchName: z.string().optional(), // Used for validation only, not sent to backend
  agency: z.string().optional(),
  bankAccountNumber: z.string().optional(),
  bankAccountName: z.string().optional(),
  kswpNumber: z.string().optional(),
  position: z.string().optional(),
  department: z.string().optional(),
  employeeNumber: z.string().optional(),
  taxCategory: z.enum(userTaxCategory as [string, ...string[]]).optional(),
})
  .refine(
    (data) => {
      // If role is not client and not admin, branchId is required
      if (data.role !== "client" && data.role !== "admin") {
        return !!data.branchId;
      }
      return true;
    },
    {
      message: "Required",
      path: ["branchId"],
    }
  )
  .refine(
    (data) => {
      // If role is agent, agency is required
      if (data.role === "agent") {
        return !!data.agency;
      }
      return true;
    },
    {
      message: "Required",
      path: ["agency"],
    }
  )
  .superRefine((data, ctx) => {
    // Helper function to check if validation is needed
    const isSerembanStaffOrLawyer =
      (data.role === "staff" || data.role === "lawyer") &&
      data.branchName === "Seremban";

    if (isSerembanStaffOrLawyer) {
      // Define required fields for Seremban staff/lawyer
      const requiredFields = [
        { field: "bankAccountNumber", label: "Bank Account Number" },
        { field: "bankAccountName", label: "Bank Account Name" },
        { field: "kswpNumber", label: "KWSP Number" },
        { field: "position", label: "Position" },
        { field: "department", label: "Department" },
        { field: "employeeNumber", label: "Employee Number" },
        { field: "taxCategory", label: "Tax Category" },
      ];

      // Validate each required field
      requiredFields.forEach(({ field }) => {
        if (!data[field as keyof typeof data]) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Required",
            path: [field],
          });
        }
      });
    }
  });

// Schema for user responses from API
export const UserResponseSchema = BaseUserSchema.extend({
  id: z.string(),
  branch: z.custom<IBranch>().optional(),
  agency: z.string().optional(),
  bankAccountNumber: z.string().optional(),
  bankAccountName: z.string().optional(),
  kswpNumber: z.string().optional(),
  position: z.string().optional(),
  department: z.string().optional(),
  employeeNumber: z.string().optional(),
  taxCategory: z.enum(userTaxCategory as [string, ...string[]]).optional(),
  claimBalance: z
    .object({
      medical: z
        .object({
          total: z.number(),
          used: z.number(),
          balance: z.number(),
        })
        .optional(),
    })
    .optional(),
  leaveBalance: z
    .object({
      annual: z
        .object({
          total: z.number(),
          used: z.number(),
          balance: z.number(),
        })
        .optional(),
      medical: z
        .object({
          total: z.number(),
          used: z.number(),
          balance: z.number(),
        })
        .optional(),
      hospitalization: z
        .object({
          total: z.number(),
          used: z.number(),
          balance: z.number(),
        })
        .optional(),
      maternity: z
        .object({
          total: z.number(),
          used: z.number(),
          balance: z.number(),
        })
        .optional(),
    })
    .optional(),
});

export type IUserRequest = z.infer<typeof UserRequestSchema>;
export type IUserResponse = z.infer<typeof UserResponseSchema>;

export interface IApiResponseGetUsers {
  results: IUserResponse[];
  page: string;
  limit: number;
  totalPages: number;
  totalResults: number;
}
